import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import securityContext from './security-context.service'
import { HttpGetParams, HttpPostParams, HttpPutParams, HttpPatchParams, HttpDeleteParams } from './http.types'

class HttpService {
  private http: AxiosInstance = axios.create({
    baseURL: process.env.VUE_APP_API_URL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      environment: window.location.href.includes('/dev/') ? 'dev' : 'live',
    }
  })

  constructor() {
    this.http.interceptors.response.use(
      response => response,
      error => {
        if (error?.response?.data === 'User not found' && !error?.config?.skipDeactivationEvent) {
          window.dispatchEvent(new CustomEvent('kiosk-deactivated'))
        }
        return Promise.reject(error)
      }
    )
  }

  getInstance(): AxiosInstance {
    return this.http
  }

  private setAuth(requireToken: boolean, event_identifier?: string): void {
    if (requireToken) {
      const token = securityContext.token(event_identifier) ?? securityContext.kioskToken()
      if (token) {
        this.http.defaults.headers.common.Authorization = `Bearer ${token}`
      }
    } else {
      delete this.http.defaults.headers.common.Authorization
    }
  }

  async get<T>(params: HttpGetParams, event_identifier?: string): Promise<AxiosResponse<T>> {
    this.setAuth(params.requireToken, event_identifier)
    const config: AxiosRequestConfig = { headers: { url: window.location.href }, ...params.config }
    return this.http.get<T>(params.url, config)
  }

  async post<T>(params: HttpPostParams, event_identifier?: string): Promise<AxiosResponse<T>> {
    this.setAuth(params.requireToken, event_identifier)
    const config: AxiosRequestConfig = { headers: { url: window.location.href }, ...params.config }
    return this.http.post<T>(params.url, params.payload, config)
  }

  async put<T>(params: HttpPutParams, event_identifier?: string): Promise<AxiosResponse<T>> {
    this.setAuth(params.requireToken, event_identifier)
    const config: AxiosRequestConfig = { headers: { url: window.location.href }, ...params.config }
    return this.http.put<T>(params.url, params.payload, config)
  }

  async patch<T>(params: HttpPatchParams, event_identifier?: string): Promise<AxiosResponse<T>> {
    this.setAuth(params.requireToken, event_identifier)
    const config: AxiosRequestConfig = { headers: { url: window.location.href }, ...params.config }
    return this.http.patch<T>(params.url, params.payload, config)
  }

  async delete<T>(params: HttpDeleteParams, event_identifier?: string): Promise<AxiosResponse<T>> {
    this.setAuth(params.requireToken, event_identifier)
    const config: AxiosRequestConfig = { headers: { url: window.location.href }, ...params.config }
    return this.http.delete<T>(params.url, config)
  }
}

export default new HttpService()
